import Button, { ButtonTypes } from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal, { CloseDialogEvent } from "molecules/modals/modal";
import React, { useState } from "react";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Heading from "atoms/typography/heading";
import InputFormField from "molecules/form-fields/input-form-field";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { useHistory } from "react-router-dom";
import { ToastManager } from "utilities/toast/toast-manager";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import NotebookRecord from "models/view-models/notebook-record";
import { t } from "utilities/localization-utils";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";

interface NotebookCreateModalProps {
    isVisible: boolean;
    closeDialog: (event?: CloseDialogEvent) => void;
    onCreateSuccess: () => void;
}

const BASE_CLASS_NAME = "c-create-modal";

const NotebookCreateModal: React.FC<NotebookCreateModalProps> = ({
    isVisible,
    closeDialog,
    onCreateSuccess,
}) => {
    const [notebookTitle, setNotebookTitle] = React.useState<string>("");
    const { createNotebook } = useNotebooksAtom();
    const { globalState } = useGlobalState();
    const currentUserId = globalState.currentIdentity?.userId();
    const [isValidNotebookName, setIsValidNotebookName] = useState(true);
    const history = useHistory();

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const onNotebookNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNotebookTitle(value);

        if (!isValidNotebookName && value.trim().length > 0) {
            setIsValidNotebookName(true);
        }
    };

    const createNewNotebook = async () => {
        const isValid = notebookTitle.trim().length > 0;
        setIsValidNotebookName(isValid);

        if (!isValid) return;

        try {
            const notebook = await createNotebook(
                new NotebookRecord({
                    ownerId: currentUserId,
                    name: notebookTitle,
                })
            );

            if (!notebook) {
                throw new Error("Page creation failed");
            }

            const route = RouteUtils.getUrl(siteMap.notebooks.notebook.detail, {
                notebookId: Number(notebook.id),
            });
            onCreateSuccess();
            resetModal();
            closeDialog();

            if (route) {
                history.push(route);
            }
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("creating"),
                    resource: t("notebook"),
                })
            );
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await createNewNotebook();
    };

    const resetModal = () => {
        setIsValidNotebookName(true);
        setNotebookTitle("");
    };

    const onClickCancel = () => {
        resetModal();
        closeDialog();
    };

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={closeDialog}
            cssClassName={BASE_CLASS_NAME}
            isVisible={isVisible}
            onCloseComplete={onClickCancel}
            label={t("createNewNotebook")}
            type={modalType}>
            <Heading cssClassName={`${BASE_CLASS_NAME}__heading`}>
                {t("createNewNotebook")}
                <Button
                    accessibleText={t("close")}
                    cssClassName={"-modal-close -icon"}
                    onClick={closeDialog}
                    size={ButtonSizes.Medium}
                    style={ButtonStyles.TertiaryAlt}>
                    <Icon type={Icons.Close} size={IconSizes.Large} />
                </Button>
            </Heading>
            <form onSubmit={handleSubmit}>
                <div className={`${BASE_CLASS_NAME}__input`}>
                    <InputFormField
                        placeholder={t("notebooks-create-notebook-placeholder")}
                        onChange={onNotebookNameChange}
                        label={t("notebook-name")}
                        value={notebookTitle}
                        maxLength={60}
                        isValid={isValidNotebookName}
                    />
                </div>
                <div className={`${BASE_CLASS_NAME}__buttons`}>
                    <Button
                        accessibleText={t("cancel")}
                        onClick={onClickCancel}
                        style={ButtonStyles.Secondary}>
                        {t("cancel")}
                    </Button>
                    <Button
                        accessibleText={t("create")}
                        style={ButtonStyles.Primary}
                        cssClassName={`${BASE_CLASS_NAME}__create-button`}
                        type={ButtonTypes.Submit}>
                        <Icon type={Icons.Plus} />
                        {t("create")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default NotebookCreateModal;
